import { useParams, useNavigate, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import { FadeUp, SectionHead } from "../components/site/Reveal";
import { scrollToId } from "../lib/scroll";
import { BRANDS, PARENT_BRAND } from "../data/content";

export default function BrandProfile() {
    const { brandId } = useParams();
    const navigate = useNavigate();
    const brand = BRANDS.find((b) => b.id === brandId);

    if (!brand) return <Navigate to="/" replace />;

    const goHomeSection = (id) => {
        navigate("/");
        setTimeout(() => scrollToId(id), 500);
    };

    return (
        <div className="grain bg-navy-deep" data-testid="brand-profile-page">
            <Header />
            <main>
                <section className="bg-ivory pb-20 pt-[76px] text-navy-deep lg:pb-28" data-testid="brand-profile-hero">
                    <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                        <FadeUp>
                            <button
                                type="button"
                                data-testid="brand-back-button"
                                onClick={() => goHomeSection("#brands")}
                                className="mt-10 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-navy-deep/60 transition-colors hover:text-gold"
                            >
                                <ArrowLeft size={14} />
                                Our Brands
                            </button>
                        </FadeUp>

                        <div className="mt-10 grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
                            <FadeUp className="lg:col-span-5">
                                <div className="flex flex-col items-start gap-8 border border-navy-deep/10 bg-white p-10 lg:p-14">
                                    {brand.logo ? (
                                        <img
                                            src={brand.logo}
                                            alt={`${brand.name} — official logo`}
                                            className="h-16 w-auto object-contain mix-blend-multiply lg:h-20"
                                            data-testid="brand-profile-logo"
                                        />
                                    ) : (
                                        <span className="font-serif text-4xl font-medium tracking-wide text-navy-deep" data-testid="brand-profile-logo">
                                            {brand.name}
                                        </span>
                                    )}
                                    <span className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.28em] text-gold">
                                        <span className="h-px w-8 bg-gold" aria-hidden="true" />
                                        A Viet Medica Ltd Brand
                                    </span>
                                </div>
                            </FadeUp>

                            <div className="lg:col-span-7">
                                <FadeUp delay={0.1}>
                                    <h1 className="font-serif text-4xl font-light leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
                                        {brand.name}
                                    </h1>
                                    <p className="mt-7 max-w-2xl text-base leading-relaxed text-navy-deep/75 md:text-lg">
                                        {brand.full}
                                    </p>
                                    <div className="mt-10 flex flex-wrap gap-4">
                                        {brand.website ? (
                                            <a
                                                href={brand.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid="brand-website-button"
                                                className="inline-flex items-center gap-2 bg-navy-deep px-6 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-ivory transition-colors duration-300 hover:bg-gold hover:text-navy-deep"
                                            >
                                                Visit Official Website
                                                <ArrowUpRight size={14} />
                                            </a>
                                        ) : (
                                            <span
                                                data-testid="brand-website-button"
                                                className="inline-flex items-center gap-2 border border-dashed border-navy-deep/30 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-navy-deep/45"
                                            >
                                                Website Coming Soon
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            data-testid="brand-enquiry-button"
                                            onClick={() => goHomeSection("#contact")}
                                            className="inline-flex items-center gap-2 border border-navy-deep/25 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-navy-deep transition-colors duration-300 hover:border-gold hover:text-gold"
                                        >
                                            Make an Enquiry
                                        </button>
                                    </div>
                                </FadeUp>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="bg-navy-deep py-20 lg:py-28" data-testid="brand-services">
                    <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                        <SectionHead eyebrow="Support Areas" title={`How ${brand.name} Supports You`} />
                        <div className="mt-12 grid grid-cols-1 gap-px bg-white/8 sm:grid-cols-2 lg:grid-cols-3">
                            {brand.services.map((s, i) => (
                                <FadeUp key={s} delay={(i % 3) * 0.07} className="h-full">
                                    <div
                                        className="flex h-full items-center gap-4 bg-navy-deep p-6 transition-colors duration-500 hover:bg-navy-surface"
                                        data-testid={`brand-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    >
                                        <Check size={16} className="shrink-0 text-gold" aria-hidden="true" />
                                        <span className="text-sm text-slate-200">{s}</span>
                                    </div>
                                </FadeUp>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="bg-navy-surface py-20 lg:py-24" data-testid="brand-parent-section">
                    <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                        <FadeUp>
                            <div className="flex flex-col items-start gap-8 border border-gold/30 p-8 lg:flex-row lg:items-center lg:justify-between lg:p-12">
                                <div className="flex items-center gap-7">
                                    <span className="inline-flex shrink-0 rounded-[3px] bg-ivory px-4 py-3">
                                        <img
                                            src={PARENT_BRAND.logo}
                                            alt="Viet Medica Ltd — official logo"
                                            loading="lazy"
                                            className="h-11 w-auto"
                                        />
                                    </span>
                                    <p className="max-w-xl text-sm leading-relaxed text-slate-300/85 lg:text-base">
                                        {PARENT_BRAND.copy}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    data-testid="brand-parent-cta"
                                    onClick={() => navigate("/about")}
                                    className="inline-flex shrink-0 items-center gap-2 border border-gold/60 px-6 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-gold transition-colors duration-300 hover:bg-gold hover:text-navy-deep"
                                >
                                    About Viet Medica
                                    <ArrowUpRight size={14} />
                                </button>
                            </div>
                        </FadeUp>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
